/* Repository path: frontend/src/api/estimate.ts */

import { ApiError } from "./client";
import type {
  EffectiveTiming,
  EstimatePreview,
  FieldOption,
  ForwardContributor,
  ForwardExcludedItem,
  ForwardPlanBlock,
  ForwardRecordedBenefit,
  ForwardSettledItem,
  ItemActual,
  ItemActualVariance,
  ItemReminder,
  ItemReminderStatus,
  ItemTiming,
  MissingInput,
  PlanItem,
  PlannedReminderEntry,
  PlanningAllowanceItem,
  PlanningBlock,
  PlanningOneTimeAllowanceItem,
  PlanningUnbudgetedRisk,
  PlanProfileResponse,
  ProfileEditResult,
  RefinementResult,
  RemindersBlock,
  SavedPlan,
  SourceReference,
  SuppressedReminderItem,
  TimelineBlock,
  TimelineMonth,
  TimelineNext,
  UpcomingReminder,
  UserDeadline,
} from "../types";

const MONEY = /^\d{1,12}\.\d{2}$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const REMINDER_OUTCOMES = new Set(["none", "sent", "failed", "unknown"]);
const KINDS = new Set(["obligation", "benefit", "action", "warning"]);
const STATES = new Set(["calculated", "needs_more_information", "identified_only"]);
const CONFIDENCES = new Set(["low", "medium", "high"]);
const PLANNING_BASES = new Set([
  "official_calculator_bounds",
  "published_authority_range",
  "conservative_category_allowance",
  "reviewed_no_payment",
]);
const PLANNING_PERIODS = new Set(["annual", "monthly", "one_time"]);
const PLANNING_RISK_REASONS = new Set([
  "no_reviewed_band",
  "band_source_lapsed",
  "band_profile_gate_failed",
  "category_unclassified",
]);
const TIMING_KINDS = new Set([
  "fixed_date",
  "expected_window",
  "recurring",
  "event_triggered",
  "admin_deadline",
  "user_deadline",
  "unknown",
]);
const TIMING_ORIGINS = new Set([
  "official_source",
  "profile_derived",
  "user_supplied",
  "not_established",
]);
const TIMING_PRECISIONS = new Set(["day", "month", "quarter", "none"]);
const TIMING_CADENCES = new Set(["monthly", "quarterly", "annual"]);
const MONTH_KEY = /^\d{4}-\d{2}$/;
const ACTUAL_STATES = new Set(["open", "received", "paid", "completed"]);
const VARIANCE_AGAINST = new Set(["amount", "range", "none"]);
const VARIANCE_DIRECTIONS = new Set([
  "higher",
  "lower",
  "same",
  "within_range",
  "not_comparable",
]);
const FORWARD_BASES = new Set(["actual", "estimate", "planning_allowance"]);
const FORWARD_EXCLUDED_REASONS = new Set(["settled"]);
// W5-01: the five item-level reminder kinds and their suppression
// reasons. `ITEM_REMINDER_OUTCOMES` is deliberately a separate set from
// `REMINDER_OUTCOMES` above, not an extension of it: an item-level
// dispatch can reach `"cancelled"`, a state the plan-level `reminder`
// block's `last_outcome` never carries, so the two must not share one
// set. See docs/architecture/features/W5-01.md §2, §4, §5.
const REMINDER_KINDS = new Set([
  "user_supplied_deadline",
  "fixed_deadline",
  "expected_window",
  "action",
  "review",
]);
const REMINDER_SUPPRESSED_REASONS = new Set([
  "settled",
  "no_resolved_date",
  "unknown_timing",
  "not_reminded_kind",
  "past",
  "beyond_horizon",
  "item_reminder_off",
  "no_more_planned",
]);
const ITEM_REMINDER_OUTCOMES = new Set(["none", "sent", "failed", "unknown", "cancelled"]);
const ITEM_REMINDER_STATES = new Set(["active", "item_off", "plan_off", "no_longer_needed"]);
const ITEM_REMINDER_ENDED_REASONS = new Set(["settled", "date_passed"]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function nonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function isMoney(value: unknown): value is string {
  return typeof value === "string" && MONEY.test(value);
}

function isAbsent(value: unknown): boolean {
  return value === null || value === undefined;
}

function cents(value: string): bigint {
  const [whole, fraction] = value.split(".");
  return BigInt(whole) * 100n + BigInt(fraction);
}

function isBreakdownRow(value: unknown): value is { label: string; amount: string } {
  return isRecord(value) && nonEmptyString(value.label) && isMoney(value.amount);
}

function isSource(value: unknown): value is SourceReference {
  if (!isRecord(value)) return false;
  if (!nonEmptyString(value.publisher) || !nonEmptyString(value.title)) return false;
  if (!DATE.test(String(value.reviewed_at))) return false;
  if (!nonEmptyString(value.url)) return false;
  try {
    return new URL(value.url).protocol === "https:";
  } catch {
    return false;
  }
}

/** W4-01: optional everywhere it appears (an item, `missing_input`, or one
 * of its structured `fields`). Absent is valid (older snapshot, or nothing
 * bound); present must be an array of non-empty strings — anything else is
 * treated as a malformed payload rather than silently passed through, since
 * a broken value here would otherwise reach `ExplainerDisclosure` unnoticed.
 */
function isExplainerKeysField(value: unknown): boolean {
  return (
    value === undefined
    || (Array.isArray(value) && value.every((item) => typeof item === "string" && item.length > 0))
  );
}

/**
 * Validate one item against the rules for its own state.
 *
 * There is deliberately no check on the item code and no check on how many
 * items a plan has. The delivered validator required exactly two components
 * drawn from a fixed code set; under general applicability an unfamiliar code
 * and a forty-item plan are both normal.
 */
function isItem(value: unknown): value is PlanItem {
  if (!isRecord(value)) return false;
  if (
    !(
      nonEmptyString(value.code)
      && KINDS.has(String(value.kind))
      && STATES.has(String(value.calculation_state))
      && nonEmptyString(value.official_name)
      && nonEmptyString(value.english_name)
      && nonEmptyString(value.why_it_may_apply)
      && value.currency === "EUR"
      && CONFIDENCES.has(String(value.confidence))
      && Array.isArray(value.assumptions)
      && Array.isArray(value.reasons)
      && Array.isArray(value.sources)
      && value.sources.length > 0
      && value.sources.every(isSource)
      && typeof value.included_in_reserve === "boolean"
      && typeof value.included_in_total === "boolean"
      && Array.isArray(value.breakdown)
      && value.breakdown.every(isBreakdownRow)
      && isExplainerKeysField(value.explainer_keys)
    )
  ) {
    return false;
  }

  const state = String(value.calculation_state);

  if (state === "calculated") {
    // Sub-charges must add up to the item total, or we show neither.
    if (value.breakdown.length > 0) {
      const sum = value.breakdown.reduce((total, row) => total + cents(row.amount), 0n);
      if (!isMoney(value.amount) || sum !== cents(value.amount)) return false;
    }
    const hasPoint = isMoney(value.amount);
    const hasRange = isMoney(value.min_amount) && isMoney(value.max_amount);
    if (!hasPoint && !hasRange) return false;
    const hasMonthlyExact = isMoney(value.monthly_equivalent)
      && isAbsent(value.monthly_min_amount) && isAbsent(value.monthly_max_amount);
    const hasMonthlyRange = isMoney(value.monthly_min_amount) && isMoney(value.monthly_max_amount);
    if (!hasMonthlyExact && !hasMonthlyRange) return false;
    if (hasPoint === hasRange || hasMonthlyExact === hasMonthlyRange) return false;
    if (hasPoint !== hasMonthlyExact) return false;
    if (hasRange && (cents(value.min_amount as string) > cents(value.max_amount as string)
      || cents(value.monthly_min_amount as string) > cents(value.monthly_max_amount as string))) return false;
    if (!nonEmptyString(value.rule_version)) return false;
    if (!isAbsent(value.missing_input)) return false;
    return true;
  }

  // Both uncalculated states must carry no amount at all. A zero or a
  // placeholder here would be indistinguishable from a sourced figure.
  if (
    !isAbsent(value.amount)
    || !isAbsent(value.min_amount)
    || !isAbsent(value.max_amount)
    || !isAbsent(value.monthly_equivalent)
    || !isAbsent(value.monthly_min_amount)
    || !isAbsent(value.monthly_max_amount)
  ) {
    return false;
  }
  if (value.included_in_reserve !== false) return false;
  if (value.included_in_total !== false) return false;

  // An uncalculated item has no amount, so it can have no breakdown either.
  if (value.breakdown.length > 0) return false;

  if (state === "needs_more_information") {
    return (
      isRecord(value.missing_input)
      && nonEmptyString(value.missing_input.code)
      && nonEmptyString(value.missing_input.label)
      && ["decimal", "integer", "boolean", "date", "enum", "structured"].includes(
        String(value.missing_input.value_type),
      )
      && Array.isArray(value.missing_input.consumers)
      && value.missing_input.consumers.every((consumer) => isRecord(consumer)
        && nonEmptyString(consumer.code) && nonEmptyString(consumer.label))
      && Array.isArray(value.missing_input.fields)
      && value.missing_input.fields.every((field) => isRecord(field)
        && nonEmptyString(field.code) && nonEmptyString(field.label)
        && ["decimal", "integer", "boolean", "date", "enum", "structured"].includes(String(field.value_type))
        && (field.required === undefined || typeof field.required === "boolean")
        && (field.count === undefined || (Number.isInteger(field.count) && Number(field.count) >= 0))
        && isExplainerKeysField(field.explainer_keys))
      && isExplainerKeysField(value.missing_input.explainer_keys)
    );
  }

  // identified_only
  return nonEmptyString(value.next_action);
}

function bounds(value: Record<string, unknown>, exactKey = "amount"): [bigint, bigint] | null {
  if (isMoney(value[exactKey]) && isAbsent(value.min) && isAbsent(value.max)) {
    const exact = cents(value[exactKey] as string);
    return [exact, exact];
  }
  if (isAbsent(value[exactKey]) && isMoney(value.min) && isMoney(value.max)) {
    const minimum = cents(value.min);
    const maximum = cents(value.max);
    return minimum <= maximum ? [minimum, maximum] : null;
  }
  return null;
}

function aggregateMatches(value: unknown, selected: PlanItem[], monthly = false): boolean {
  if (!isRecord(value) || value.currency !== "EUR" || !Array.isArray(value.reconciles_to)
    || !value.reconciles_to.every(nonEmptyString)) return false;
  const result = bounds(value);
  if (!result) return false;
  const codes = [...selected.map((item) => item.code)].sort();
  const named = [...value.reconciles_to.map(String)].sort();
  if (codes.length !== named.length || codes.some((code, index) => code !== named[index])) return false;
  const expected = selected.reduce<[bigint, bigint]>((sum, item) => {
    const exact = monthly ? item.monthly_equivalent : item.amount;
    const minimum = monthly ? item.monthly_min_amount : item.min_amount;
    const maximum = monthly ? item.monthly_max_amount : item.max_amount;
    return [sum[0] + cents((exact ?? minimum) as string), sum[1] + cents((exact ?? maximum) as string)];
  }, [0n, 0n]);
  return result[0] === expected[0] && result[1] === expected[1];
}

export function isValidEstimatePayload(value: unknown): value is EstimatePreview {
  if (!isRecord(value) || value.currency !== "EUR") return false;
  if (!nonEmptyString(value.plan_preview_id)) return false;
  if (!nonEmptyString(value.input_version)) return false;
  if (
    !isRecord(value.estimate_period)
    || !DATE.test(String(value.estimate_period.start))
    || !DATE.test(String(value.estimate_period.end))
  ) {
    return false;
  }

  if (!Array.isArray(value.items) || !value.items.every(isItem)) return false;

  const codes = new Set(value.items.map((item) => item.code));
  if (codes.size !== value.items.length) return false;

  if (!isRecord(value.totals)) return false;
  const outgoing = value.items.filter((item) => item.kind === "obligation" && item.calculation_state === "calculated" && item.included_in_total);
  const benefits = value.items.filter((item) => item.kind === "benefit" && item.calculation_state === "calculated" && item.included_in_total);
  const contributing = value.items.filter((item) => item.calculation_state === "calculated" && item.included_in_reserve);
  if (!aggregateMatches(value.totals.outgoing_12_month, outgoing)
    || !aggregateMatches(value.totals.potential_benefits, benefits)
    || !aggregateMatches(value.totals.monthly_reserve, contributing, true)) return false;

  if (!isRecord(value.reserve)) return false;
  const { reserve } = value;
  const reserveExact = isMoney(reserve.monthly) && isAbsent(reserve.min) && isAbsent(reserve.max);
  const reserveRange = isMoney(reserve.min) && isMoney(reserve.max);
  if (reserveExact === reserveRange || reserve.currency !== "EUR") return false;
  if (!Array.isArray(reserve.reconciles_to)) return false;
  if (!reserve.reconciles_to.every(nonEmptyString)) return false;

  // The reserve must reconcile to exactly the items it names, from the payload
  // alone. A total that cannot be verified from what is visible is not shown.
  const named = new Set(reserve.reconciles_to.map(String));
  if (contributing.length !== named.size) return false;
  if (!contributing.every((item) => named.has(item.code))) return false;

  const lower = contributing.reduce((sum, item) => sum + cents((item.monthly_equivalent ?? item.monthly_min_amount) as string), 0n);
  const upper = contributing.reduce((sum, item) => sum + cents((item.monthly_equivalent ?? item.monthly_max_amount) as string), 0n);
  if (reserveExact && (lower !== upper || lower !== cents(reserve.monthly as string))) return false;
  if (reserveRange && (lower !== cents(reserve.min as string) || upper !== cents(reserve.max as string))) return false;

  return true;
}

// --- W4-02: the optional planning block -------------------------------
//
// A sibling validator, not a wrapper: `isValidEstimatePayload` above is
// byte-for-byte unchanged by this story. If a planning allowance ever
// needed to touch that function, the allowance would have leaked into the
// calculation layer — see docs/architecture/features/W4-02.md §1.

function isMoneyPayload(value: unknown): value is Record<string, unknown> {
  return isRecord(value) && value.currency === "EUR" && bounds(value) !== null;
}

function isAllowance(value: unknown): value is PlanningAllowanceItem {
  return (
    isRecord(value)
    && nonEmptyString(value.item_code)
    && nonEmptyString(value.category)
    && PLANNING_BASES.has(String(value.basis))
    && PLANNING_PERIODS.has(String(value.native_period))
    && isMoney(value.monthly_min)
    && isMoney(value.monthly_max)
    && cents(value.monthly_min) <= cents(value.monthly_max)
    && value.currency === "EUR"
    && value.confidence === "low"
    && nonEmptyString(value.policy_version)
    && nonEmptyString(value.rule_version)
    && Array.isArray(value.sources)
    && value.sources.length > 0
    && value.sources.every(isSource)
  );
}

function isOneTimeAllowance(value: unknown): value is PlanningOneTimeAllowanceItem {
  return (
    isRecord(value)
    && nonEmptyString(value.item_code)
    && nonEmptyString(value.category)
    && PLANNING_BASES.has(String(value.basis))
    && isMoney(value.annual_min)
    && isMoney(value.annual_max)
    && cents(value.annual_min) <= cents(value.annual_max)
    && value.currency === "EUR"
    && value.confidence === "low"
    && nonEmptyString(value.policy_version)
    && nonEmptyString(value.rule_version)
    && Array.isArray(value.sources)
    && value.sources.length > 0
    && value.sources.every(isSource)
  );
}

function isUnbudgetedRisk(value: unknown): value is PlanningUnbudgetedRisk {
  return (
    isRecord(value)
    && nonEmptyString(value.item_code)
    && PLANNING_RISK_REASONS.has(String(value.reason))
  );
}

// --- W4-04: item timing and the derived timeline ----------------------------

function isNullableDate(value: unknown): boolean {
  return value === null || (typeof value === "string" && DATE.test(value));
}

function isUserDeadline(value: unknown): value is UserDeadline {
  return isRecord(value) && DATE.test(String(value.date)) && DATE.test(String(value.recorded_at));
}

function isEffectiveTiming(value: unknown): value is EffectiveTiming {
  return (
    isRecord(value)
    && TIMING_KINDS.has(String(value.kind))
    && isNullableDate(value.window_start)
    && isNullableDate(value.window_end)
    && TIMING_PRECISIONS.has(String(value.precision))
    && TIMING_ORIGINS.has(String(value.origin))
  );
}

/**
 * Sibling validator to `isItem`, never called by it: a malformed
 * `PlanItem.timing` must never fail the whole estimate, only degrade to no
 * timing for that one item. `null`/`undefined` is itself a normal, valid
 * state (a pre-W4-04 snapshot) — distinct from a malformed non-null value,
 * which also degrades to `null` here rather than reaching `TimingLine`.
 * See docs/architecture/features/W4-04.md §9.
 */
export function itemTimingIfValid(value: unknown): ItemTiming | null {
  if (value === null || value === undefined) return null;
  if (!isRecord(value)) return null;
  if (!TIMING_KINDS.has(String(value.kind))) return null;
  if (!TIMING_ORIGINS.has(String(value.origin))) return null;
  if (!TIMING_PRECISIONS.has(String(value.precision))) return null;
  if (!isNullableDate(value.window_start) || !isNullableDate(value.window_end)) return null;
  if (value.cadence !== null && !TIMING_CADENCES.has(String(value.cadence))) return null;
  if (value.trigger !== null && typeof value.trigger !== "string") return null;
  if (value.description !== null && typeof value.description !== "string") return null;
  if (!Array.isArray(value.sources) || !value.sources.every(isSource)) return null;
  if (value.user_deadline !== null && !isUserDeadline(value.user_deadline)) return null;
  if (!isEffectiveTiming(value.effective)) return null;
  return value as unknown as ItemTiming;
}

function isCodeArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every(nonEmptyString);
}

function isCodesGroup(value: unknown): value is { item_codes: string[] } {
  return isRecord(value) && isCodeArray(value.item_codes);
}

function isMonthEntry(value: unknown): value is TimelineMonth {
  return isRecord(value) && MONTH_KEY.test(String(value.month)) && isCodeArray(value.item_codes);
}

function isTimelineNext(value: unknown): value is TimelineNext {
  return (
    isRecord(value)
    && nonEmptyString(value.item_code)
    && TIMING_KINDS.has(String(value.kind))
    && TIMING_ORIGINS.has(String(value.origin))
  );
}

/**
 * Sibling validator to `isValidEstimatePayload`, never called by it and
 * never mutating it: a malformed `timeline` degrades to `null` (no
 * chronological view) rather than breaking the plan. Call only after
 * `isValidEstimatePayload` has accepted `estimate`. See
 * docs/architecture/features/W4-04.md §7.
 */
export function timelineIfValid(estimate: EstimatePreview): TimelineBlock | null {
  const { timeline } = estimate;
  if (timeline === undefined) return null;
  if (!isRecord(timeline)) return null;
  if (!nonEmptyString(timeline.timing_version)) return null;
  if (timeline.timezone !== "Europe/Amsterdam") return null;
  if (!DATE.test(String(timeline.generated_for))) return null;
  if (!Array.isArray(timeline.months) || timeline.months.length !== 12) return null;
  if (!timeline.months.every(isMonthEntry)) return null;
  if (!isCodesGroup(timeline.overdue)) return null;
  if (!isCodesGroup(timeline.when_triggered)) return null;
  if (!isCodesGroup(timeline.unknown)) return null;
  if (!isCodesGroup(timeline.beyond_plan_period)) return null;
  if (timeline.next !== null && !isTimelineNext(timeline.next)) return null;
  return timeline as unknown as TimelineBlock;
}

/**
 * Validate the optional `planning` block and return it only if every
 * figure in it reconciles from the payload alone — the same discipline
 * `isValidEstimatePayload` applies to the reserve. Never throws: planning
 * is never load-bearing, so anything malformed or unreconciling degrades to
 * `null` (no recommendation shown) rather than breaking the plan. Call only
 * after `isValidEstimatePayload` has accepted `estimate`. See
 * docs/architecture/features/W4-02.md §7.
 */
export function planningBlockIfValid(estimate: EstimatePreview): PlanningBlock | null {
  const { planning } = estimate;
  if (planning === undefined) return null;
  if (!isRecord(planning) || !nonEmptyString(planning.policy_version)) return null;
  if (!Array.isArray(planning.allowances) || !planning.allowances.every(isAllowance)) return null;
  if (
    !Array.isArray(planning.one_time_allowances)
    || !planning.one_time_allowances.every(isOneTimeAllowance)
  ) {
    return null;
  }
  if (
    !Array.isArray(planning.unbudgeted_risks)
    || !planning.unbudgeted_risks.every(isUnbudgetedRisk)
  ) {
    return null;
  }

  if (planning.recommended_monthly_buffer === undefined) {
    // Valid: nothing to anchor a number to yet, but risks or one-off items
    // may still be worth showing. See docs/design/features/W4-02.md, state 3.
    return planning as PlanningBlock;
  }

  const buffer = planning.recommended_monthly_buffer;
  if (!isMoneyPayload(buffer) || !isRecord(buffer.composition)) return null;
  const { calculated_reserve: calculatedReserve, planning_allowances: planningAllowances } =
    buffer.composition;
  if (!isMoneyPayload(calculatedReserve) || !isMoneyPayload(planningAllowances)) return null;
  if (
    !isRecord(buffer.reconciles_to)
    || !Array.isArray(buffer.reconciles_to.calculated)
    || !buffer.reconciles_to.calculated.every(nonEmptyString)
    || !Array.isArray(buffer.reconciles_to.allowance)
    || !buffer.reconciles_to.allowance.every(nonEmptyString)
  ) {
    return null;
  }

  const reserveBounds = bounds(calculatedReserve);
  const allowanceBounds = bounds(planningAllowances);
  const bufferBounds = bounds(buffer);
  if (!reserveBounds || !allowanceBounds || !bufferBounds) return null;
  if (
    bufferBounds[0] !== reserveBounds[0] + allowanceBounds[0]
    || bufferBounds[1] !== reserveBounds[1] + allowanceBounds[1]
  ) {
    return null;
  }

  // The buffer's calculated portion must be the same reserve already shown
  // above it, named the same way — never a second, independently-derived
  // "reserve" quietly substituted in.
  const declaredReserveBounds = bounds(estimate.reserve as unknown as Record<string, unknown>, "monthly");
  if (
    !declaredReserveBounds
    || declaredReserveBounds[0] !== reserveBounds[0]
    || declaredReserveBounds[1] !== reserveBounds[1]
  ) {
    return null;
  }
  const reserveNamed = [...estimate.reserve.reconciles_to].map(String).sort();
  const compositionNamed = [...buffer.reconciles_to.calculated].map(String).sort();
  if (
    reserveNamed.length !== compositionNamed.length
    || reserveNamed.some((code, index) => code !== compositionNamed[index])
  ) {
    return null;
  }

  const allowanceCodes = new Set(planning.allowances.map((row) => row.item_code));
  const namedAllowance = new Set(buffer.reconciles_to.allowance.map(String));
  if (
    allowanceCodes.size !== namedAllowance.size
    || [...allowanceCodes].some((code) => !namedAllowance.has(code))
  ) {
    return null;
  }

  return planning as PlanningBlock;
}

export function validateEstimatePayload(value: unknown): EstimatePreview {
  if (!isValidEstimatePayload(value)) {
    throw new ApiError("We couldn’t prepare a trustworthy overview right now. Try again later.", {
      code: "invalid_estimate_payload",
    });
  }
  return value;
}

export function validateSavedPlanPayload(value: unknown): SavedPlan {
  const estimate = validateEstimatePayload(value);
  if (!isRecord(value) || !isRecord(value.reminder)) {
    throw new ApiError("We couldn’t safely display this saved plan right now. Try again later.", {
      code: "invalid_plan_payload",
    });
  }

  const publicId = nonEmptyString(value.public_id)
    ? value.public_id
    : nonEmptyString(value.plan_id) ? value.plan_id : "";
  const { reminder } = value;
  const enabled = reminder.enabled;
  const canEnable = reminder.can_enable;
  const nextDueAt = reminder.next_due_at;
  const lastOutcome = reminder.last_outcome;
  if (
    !publicId
    || typeof enabled !== "boolean"
    || typeof canEnable !== "boolean"
    || !(nextDueAt === null || typeof nextDueAt === "string")
    || !REMINDER_OUTCOMES.has(String(lastOutcome))
  ) {
    throw new ApiError("We couldn’t safely display this saved plan right now. Try again later.", {
      code: "invalid_plan_payload",
    });
  }

  return {
    ...estimate,
    public_id: publicId,
    reminder: {
      enabled,
      can_enable: canEnable,
      next_due_at: nextDueAt,
      last_outcome: lastOutcome as SavedPlan["reminder"]["last_outcome"],
    },
    deletion_available:
      typeof value.deletion_available === "boolean" ? value.deletion_available : undefined,
  };
}

// --- W4-03: the optional refinement queue and result --------------------
//
// Siblings, not wrappers, following the W4-02 `planningBlockIfValid`
// precedent: `isValidEstimatePayload` and `isItem` above are byte-for-byte
// unchanged by this story. If either ever needed one edit to accommodate
// the refinement flow, the flow would have taken a dependency on something
// the calculation validator owns. See docs/architecture/features/W4-03.md
// §4.

/**
 * The same `missing_input` shape `isItem` checks inline for a
 * `needs_more_information` item, duplicated here as its own function rather
 * than factored out of `isItem` — touching `isItem` risks the
 * calculation-validation path this story must not touch, for the sake of
 * removing a few lines of duplication.
 */
function isMissingInputShape(value: unknown): value is MissingInput {
  if (!isRecord(value)) return false;
  return (
    nonEmptyString(value.code)
    && nonEmptyString(value.label)
    && ["decimal", "integer", "boolean", "date", "enum", "structured"].includes(
      String(value.value_type),
    )
    && Array.isArray(value.consumers)
    && value.consumers.every((consumer) => isRecord(consumer)
      && nonEmptyString(consumer.code) && nonEmptyString(consumer.label))
    && Array.isArray(value.fields)
    && value.fields.every((field) => isRecord(field)
      && nonEmptyString(field.code) && nonEmptyString(field.label)
      && ["decimal", "integer", "boolean", "date", "enum", "structured"].includes(String(field.value_type))
      && (field.required === undefined || typeof field.required === "boolean")
      && (field.count === undefined || (Number.isInteger(field.count) && Number(field.count) >= 0))
      && isExplainerKeysField(field.explainer_keys))
    && isExplainerKeysField(value.explainer_keys)
  );
}

/**
 * W6-02 (D-W6-02-04): the server-owned choices of one enum field, or `null`.
 *
 * A sibling of the validators above, never part of them: a malformed `options`
 * must degrade one question to its safe sentence, not invalidate a whole plan.
 * Valid means a non-empty array whose every entry has a non-empty string `value`
 * and a non-empty string `label`, with no repeated value. Anything else is `null`,
 * and the caller must then show no choice at all rather than a raw internal value.
 */
export function fieldOptionsIfValid(field: MissingInput["fields"][number]): FieldOption[] | null {
  const { options } = field;
  if (!Array.isArray(options) || options.length === 0) return null;
  const seen = new Set<string>();
  for (const option of options) {
    if (!isRecord(option) || !nonEmptyString(option.value) || !nonEmptyString(option.label)) return null;
    if (seen.has(option.value)) return null;
    seen.add(option.value);
  }
  return options.map(({ value, label }) => ({ value, label }));
}

/**
 * Validate and return the current refinement queue. Never throws — a
 * malformed `refinement` block degrades to `[]`, the same "nothing we can
 * usefully ask" outcome as a genuinely empty queue, rather than breaking
 * the flow. A blocker is dropped, not fatal, when it fails the
 * `missing_input` shape or when its code is not named by at least one
 * `needs_more_information` item in this same payload — the cheap
 * structural proof that a question on screen corresponds to a real
 * blocked item in this exact snapshot. Call only after
 * `isValidEstimatePayload` has accepted `estimate`. See
 * docs/architecture/features/W4-03.md §4.
 */
export function refinementQueueIfValid(estimate: EstimatePreview): MissingInput[] {
  const { refinement } = estimate;
  if (!isRecord(refinement) || !Array.isArray(refinement.blockers)) return [];

  const blockedCodes = new Set(
    estimate.items.flatMap((item) =>
      item.calculation_state === "needs_more_information" && item.missing_input
        ? [item.missing_input.code]
        : [],
    ),
  );

  return refinement.blockers.filter(
    (blocker): blocker is MissingInput =>
      isMissingInputShape(blocker) && blockedCodes.has(blocker.code),
  );
}

/**
 * Validate the optional `refinement_result` describing what an accepted
 * answer changed. Never throws. `null` means "recalculated, and we won't
 * characterise the change" — never "nothing changed"; `material_change:
 * false` is a distinct, valid outcome carried inside the object itself.
 * See docs/architecture/features/W4-03.md §4.
 */
export function refinementResultIfValid(value: unknown): RefinementResult | null {
  if (!isRecord(value) || typeof value.material_change !== "boolean") return null;
  for (const field of ["changed_item_codes", "changed_aggregate_codes", "invalidated_input_codes"] as const) {
    if (!Array.isArray(value[field]) || !value[field].every(nonEmptyString)) return null;
  }
  return value as unknown as RefinementResult;
}

// --- W4-06: the per-item actual overlay and the derived forward plan ------
//
// Siblings, not wrappers, following the W4-02/W4-03/W4-04 precedent:
// `isValidEstimatePayload` and `isItem` above are byte-for-byte unchanged
// by this story. A malformed `actual` degrades to `null` for that one
// item; a malformed or unreconciling `forward_plan` degrades to `null` for
// the whole (optional) block — neither ever breaks the plan.

function isVariance(value: unknown): value is ItemActualVariance {
  return (
    isRecord(value)
    && VARIANCE_AGAINST.has(String(value.against))
    && VARIANCE_DIRECTIONS.has(String(value.direction))
    && (value.amount === null || isMoney(value.amount))
  );
}

/**
 * Sibling validator to `isItem`, never called by it: a malformed
 * `PlanItem.actual` must never fail the whole estimate, only degrade to no
 * actual for that one item. `null`/`undefined` is itself a normal, valid
 * state (nothing recorded, or a full reversal) — distinct from a malformed
 * non-null value, which also degrades to `null` here rather than reaching
 * `RecordActual`. See docs/architecture/features/W4-06.md section 9.
 */
export function itemActualIfValid(value: unknown): ItemActual | null {
  if (value === null || value === undefined) return null;
  if (!isRecord(value)) return null;
  if (!ACTUAL_STATES.has(String(value.state))) return null;
  if (!(value.amount === null || isMoney(value.amount))) return null;
  if (!(value.period === null || nonEmptyString(value.period))) return null;
  if (!(value.monthly_equivalent === null || isMoney(value.monthly_equivalent))) return null;
  if (value.currency !== "EUR") return null;
  if (value.origin !== "user_supplied") return null;
  if (!DATE.test(String(value.recorded_at))) return null;
  if (!isVariance(value.variance)) return null;
  if (typeof value.affects_forward_plan !== "boolean") return null;
  if (!(value.still_contributes === null || typeof value.still_contributes === "boolean")) {
    return null;
  }
  return value as unknown as ItemActual;
}

// --- W5-01: the optional per-item reminder and the derived reminders
// index -------------------------------------------------------------------
//
// Siblings, not wrappers, following the same W4-02/W4-03/W4-04/W4-06
// precedent this file keeps every time: `isValidEstimatePayload` and
// `isItem` above are byte-for-byte unchanged by this story too. A
// malformed or absent `item.reminder` degrades to no reminder shown for
// that one item; a malformed or absent `reminders` block degrades to no
// index shown at all - neither ever breaks the plan. See
// docs/architecture/features/W5-01.md §7.

function isUpcomingReminder(value: unknown): value is UpcomingReminder {
  return (
    isRecord(value)
    && nonEmptyString(value.item_code)
    && REMINDER_KINDS.has(String(value.kind))
    && DATE.test(String(value.due_on))
    && TIMING_PRECISIONS.has(String(value.precision))
    && DATE.test(String(value.window_start))
    && DATE.test(String(value.window_end))
  );
}

function isSuppressedReminderItem(value: unknown): value is SuppressedReminderItem {
  return (
    isRecord(value)
    && nonEmptyString(value.item_code)
    && REMINDER_SUPPRESSED_REASONS.has(String(value.reason))
  );
}

/**
 * Sibling validator to `isItem`, never called by it, mirroring
 * `itemActualIfValid` exactly: `null`/`undefined` is itself a normal,
 * valid state (nothing derived for this item right now) - distinct from a
 * malformed non-null value, which also degrades to `null` here. See
 * docs/architecture/features/W5-01.md §7.
 */
export function itemReminderIfValid(value: unknown): ItemReminder | null {
  if (value === null || value === undefined) return null;
  if (!isRecord(value)) return null;
  if (!REMINDER_KINDS.has(String(value.kind))) return null;
  if (!TIMING_PRECISIONS.has(String(value.precision))) return null;
  if (!DATE.test(String(value.due_on))) return null;
  if (!DATE.test(String(value.window_start))) return null;
  if (!DATE.test(String(value.window_end))) return null;
  if (!Array.isArray(value.superseded) || !value.superseded.every((k) => REMINDER_KINDS.has(String(k)))) {
    return null;
  }
  if (!ITEM_REMINDER_OUTCOMES.has(String(value.last_outcome))) return null;
  return value as unknown as ItemReminder;
}

/**
 * Sibling validator to `isItem`, never called by it, mirroring
 * `itemReminderIfValid` exactly: `null`/`undefined` is itself a normal,
 * valid state (this item carries no reminder track at all) - distinct
 * from a malformed non-null value, which also degrades to `null` here.
 *
 * The one check this validator adds beyond simple shape-matching: every
 * date in the status shares the single `precision` the status itself
 * declares. A server that ever sent a day-precision `planned[]`/
 * `last_sent` entry alongside a `month`/`quarter` `precision` would be
 * violating the central honesty rule this whole story exists to enforce
 * (docs/design/features/W5-02.md, "The one thing this design has to get
 * right") - such a payload is treated as malformed and degrades to `null`
 * (no reminder UI for that item) rather than ever being rendered. See
 * docs/architecture/features/W5-02.md §4, §9.
 */
export function itemReminderStatusIfValid(value: unknown): ItemReminderStatus | null {
  if (value === null || value === undefined) return null;
  if (!isRecord(value)) return null;
  if (!ITEM_REMINDER_STATES.has(String(value.state))) return null;
  if (typeof value.can_change !== "boolean") return null;
  if (value.kind !== null && !REMINDER_KINDS.has(String(value.kind))) return null;
  if (value.precision !== null && !TIMING_PRECISIONS.has(String(value.precision))) return null;

  const about = value.about;
  if (about !== null) {
    if (!isRecord(about)) return null;
    if (!TIMING_PRECISIONS.has(String(about.precision))) return null;
    if (about.window_start !== null && !DATE.test(String(about.window_start))) return null;
    if (about.window_end !== null && !DATE.test(String(about.window_end))) return null;
    if (about.precision !== value.precision) return null;
  }

  if (
    !Array.isArray(value.superseded)
    || !value.superseded.every((k) => REMINDER_KINDS.has(String(k)))
  ) {
    return null;
  }

  const isPlannedEntry = (entry: unknown): entry is PlannedReminderEntry => (
    isRecord(entry)
    && DATE.test(String(entry.on))
    && TIMING_PRECISIONS.has(String(entry.precision))
    && entry.precision === value.precision
  );
  if (!Array.isArray(value.planned) || !value.planned.every(isPlannedEntry)) return null;
  if (value.last_sent !== null && !isPlannedEntry(value.last_sent)) return null;

  if (
    value.ended_because !== null
    && !ITEM_REMINDER_ENDED_REASONS.has(String(value.ended_because))
  ) {
    return null;
  }

  return value as unknown as ItemReminderStatus;
}

/**
 * Validate the optional `reminders` block and return it only if its
 * `item_level.next` is exactly `upcoming[0]` (or `null` when `upcoming` is
 * empty) - provable from this payload alone, the same discipline
 * `forwardPlanIfValid` applies to its headline figure. Never throws:
 * anything malformed degrades to `null` (no reminders index shown)
 * rather than breaking the plan. Call only after `isValidEstimatePayload`
 * has accepted `estimate`. See docs/architecture/features/W5-01.md §7.
 */
export function remindersBlockIfValid(estimate: EstimatePreview): RemindersBlock | null {
  if (!isRecord(estimate)) return null;
  const { reminders } = estimate;
  if (reminders === undefined) return null;
  if (!isRecord(reminders) || !nonEmptyString(reminders.rules_version)) return null;
  if (!DATE.test(String(reminders.generated_for))) return null;
  if (reminders.timezone !== "Europe/Amsterdam") return null;
  if (typeof reminders.horizon_days !== "number" || reminders.horizon_days <= 0) return null;

  const itemLevel = reminders.item_level;
  if (!isRecord(itemLevel) || typeof itemLevel.enabled !== "boolean") return null;
  if (!Array.isArray(itemLevel.upcoming) || !itemLevel.upcoming.every(isUpcomingReminder)) {
    return null;
  }
  if (!Array.isArray(itemLevel.suppressed) || !itemLevel.suppressed.every(isSuppressedReminderItem)) {
    return null;
  }
  const upcoming = itemLevel.upcoming;
  const next = itemLevel.next;
  if (upcoming.length === 0) {
    if (next !== null) return null;
  } else {
    if (!isUpcomingReminder(next)) return null;
    if (JSON.stringify(next) !== JSON.stringify(upcoming[0])) return null;
  }

  return reminders as unknown as RemindersBlock;
}

/** Mirrors `bounds()` above, for a contributor row's `monthly` /
 * `monthly_min`/`monthly_max` shape rather than `amount`/`min`/`max`. Kept
 * separate rather than generalising `bounds()` itself, since touching a
 * function `isValidEstimatePayload` depends on is exactly what this
 * story's sibling-only discipline forbids. */
function contributorBounds(row: Record<string, unknown>): [bigint, bigint] | null {
  if (isMoney(row.monthly) && isAbsent(row.monthly_min) && isAbsent(row.monthly_max)) {
    const exact = cents(row.monthly as string);
    return [exact, exact];
  }
  if (isAbsent(row.monthly) && isMoney(row.monthly_min) && isMoney(row.monthly_max)) {
    return [cents(row.monthly_min as string), cents(row.monthly_max as string)];
  }
  return null;
}

function isContributor(value: unknown): value is ForwardContributor {
  return (
    isRecord(value)
    && nonEmptyString(value.item_code)
    && FORWARD_BASES.has(String(value.basis))
    && contributorBounds(value) !== null
  );
}

function isSettledEntry(value: unknown): value is ForwardSettledItem {
  return (
    isRecord(value)
    && nonEmptyString(value.item_code)
    && ACTUAL_STATES.has(String(value.state))
    && DATE.test(String(value.recorded_at))
    && typeof value.still_contributes === "boolean"
  );
}

function isExcludedEntry(value: unknown): value is ForwardExcludedItem {
  return (
    isRecord(value)
    && nonEmptyString(value.item_code)
    && FORWARD_EXCLUDED_REASONS.has(String(value.reason))
  );
}

function isRecordedBenefit(value: unknown): value is ForwardRecordedBenefit {
  return (
    isRecord(value)
    && nonEmptyString(value.item_code)
    && ACTUAL_STATES.has(String(value.state))
    && (value.amount === null || isMoney(value.amount))
    && (value.period === null || nonEmptyString(value.period))
  );
}

function isForwardMoney(value: unknown): value is Record<string, unknown> {
  return isRecord(value) && value.currency === "EUR" && bounds(value) !== null;
}

/**
 * Validate the optional `forward_plan` block and return it only if its
 * headline figure reconciles to its own named contributors, from the
 * payload alone — the same discipline `isValidEstimatePayload` applies to
 * the reserve and `planningBlockIfValid` applies to the buffer. Never
 * throws: the forward plan is never load-bearing, so anything malformed or
 * unreconciling degrades to `null` (no third summary tier shown) rather
 * than breaking the plan. Call only after `isValidEstimatePayload` has
 * accepted `estimate`. See docs/architecture/features/W4-06.md section 9.
 */
export function forwardPlanIfValid(estimate: EstimatePreview): ForwardPlanBlock | null {
  const { forward_plan: forwardPlan } = estimate;
  if (forwardPlan === undefined) return null;
  if (!isRecord(forwardPlan) || !nonEmptyString(forwardPlan.rules_version)) return null;
  if (!DATE.test(String(forwardPlan.generated_for))) return null;
  if (forwardPlan.currency !== "EUR") return null;

  const monthlySetAside = forwardPlan.monthly_set_aside;
  if (!isForwardMoney(monthlySetAside)) return null;
  if (!isRecord(forwardPlan.composition)) return null;

  const {
    from_actuals: fromActuals,
    from_estimates: fromEstimates,
    from_planning_allowances: fromAllowances,
  } = forwardPlan.composition;
  if (!isForwardMoney(fromActuals) || !isForwardMoney(fromEstimates)) return null;
  if (!isForwardMoney(fromAllowances)) return null;
  if (!Array.isArray(forwardPlan.contributors) || !forwardPlan.contributors.every(isContributor)) {
    return null;
  }
  if (!Array.isArray(forwardPlan.settled) || !forwardPlan.settled.every(isSettledEntry)) {
    return null;
  }
  if (!Array.isArray(forwardPlan.excluded) || !forwardPlan.excluded.every(isExcludedEntry)) {
    return null;
  }
  if (
    !Array.isArray(forwardPlan.benefits_recorded)
    || !forwardPlan.benefits_recorded.every(isRecordedBenefit)
  ) {
    return null;
  }
  if (
    !Array.isArray(forwardPlan.reconciles_to)
    || !forwardPlan.reconciles_to.every(nonEmptyString)
  ) {
    return null;
  }

  // The contributor list must name exactly the codes `reconciles_to`
  // names, and the headline figure must equal their sum — both provable
  // from this payload alone.
  const contributors = forwardPlan.contributors;
  const contributorCodes = [...contributors.map((row) => String(row.item_code))].sort();
  const namedCodes = [...forwardPlan.reconciles_to.map(String)].sort();
  if (
    contributorCodes.length !== namedCodes.length
    || contributorCodes.some((code, index) => code !== namedCodes[index])
  ) {
    return null;
  }

  const totalBounds = bounds(monthlySetAside);
  const summed = contributors.reduce<[bigint, bigint]>(
    (sum, row) => {
      const rowBounds = contributorBounds(row as unknown as Record<string, unknown>) as [
        bigint,
        bigint,
      ];
      return [sum[0] + rowBounds[0], sum[1] + rowBounds[1]];
    },
    [0n, 0n],
  );
  if (!totalBounds || totalBounds[0] !== summed[0] || totalBounds[1] !== summed[1]) return null;

  return forwardPlan as unknown as ForwardPlanBlock;
}

// --- W4-07: profile edit result and the saved-plan profile read ----------
//
// Siblings, not wrappers, following the W4-02/W4-03/W4-04/W4-06 precedent
// this file has kept every time since: `isValidEstimatePayload` and
// `isItem` above are byte-for-byte unchanged by this story too. A
// malformed or absent `profile_edit_result` degrades to no "what changed"
// summary; it never breaks the plan itself.

const PROFILE_EDIT_STRING_ARRAY_FIELDS = [
  "changed_profile_fields",
  "cleared_conditional_fields",
  "retained_input_codes",
  "invalidated_input_codes",
  "added_item_codes",
  "removed_item_codes",
  "changed_item_codes",
  "changed_aggregate_codes",
] as const;

function isDormantRecords(value: unknown): value is { actuals: string[]; deadlines: string[] } {
  return (
    isRecord(value)
    && Array.isArray(value.actuals)
    && value.actuals.every((item) => typeof item === "string")
    && Array.isArray(value.deadlines)
    && value.deadlines.every((item) => typeof item === "string")
  );
}

/**
 * Validate the optional `profile_edit_result` describing what a profile
 * edit changed. Never throws. `null` means "recalculated, and we won't
 * characterise the change" — never "nothing changed"; `material_change:
 * false` is a distinct, valid outcome carried inside the object itself,
 * exactly as `refinementResultIfValid` already treats its own
 * `material_change`. `invalidation_reasons` is checked only for shape
 * (a plain string-to-string map) — an unrecognised reason code is a
 * presentation concern for `ProfileChangeSummary` to fall back on, not a
 * validity concern here. See docs/architecture/features/W4-07.md section 7.
 */
export function profileEditResultIfValid(value: unknown): ProfileEditResult | null {
  if (!isRecord(value) || typeof value.material_change !== "boolean") return null;
  for (const field of PROFILE_EDIT_STRING_ARRAY_FIELDS) {
    if (!Array.isArray(value[field]) || !value[field].every((item) => typeof item === "string")) {
      return null;
    }
  }
  if (!isRecord(value.invalidation_reasons)) return null;
  if (!Object.values(value.invalidation_reasons).every((reason) => typeof reason === "string")) {
    return null;
  }
  if (typeof value.estimate_period_changed !== "boolean") return null;
  if (!isDormantRecords(value.dormant_records)) return null;
  return value as unknown as ProfileEditResult;
}

function isAnsweredDetailInput(
  value: unknown,
): value is { code: string; label: string; depends_on_profile_fields: string[] } {
  return (
    isRecord(value)
    && nonEmptyString(value.code)
    && nonEmptyString(value.label)
    && Array.isArray(value.depends_on_profile_fields)
    && value.depends_on_profile_fields.every((field) => typeof field === "string")
  );
}

/**
 * Throwing validator for `GET /plans/{publicId}/profile` — the profile
 * editor cannot render at all without this data, so a malformed response
 * behaves like `validateSavedPlanPayload`'s own failure mode (an
 * `ApiError` the editor's loading state turns into a retry), never a
 * degrading sibling. `profile` itself is passed through only as a plain
 * record: `apiToAnswers` in `frontend/src/api/planner.ts` coerces every
 * field defensively, so it does not need a second, separate validity gate
 * here.
 */
export function validatePlanProfilePayload(value: unknown): PlanProfileResponse {
  if (
    !isRecord(value)
    || value.schema_version !== "v2"
    || !nonEmptyString(value.input_version)
    || !isRecord(value.profile)
    || !Array.isArray(value.answered_detail_inputs)
    || !value.answered_detail_inputs.every(isAnsweredDetailInput)
    || !(
      value.estimate_period === null
      || (
        isRecord(value.estimate_period)
        && DATE.test(String(value.estimate_period.start))
        && DATE.test(String(value.estimate_period.end))
      )
    )
  ) {
    throw new ApiError("We couldn’t load your answers to edit them. Try again.", {
      code: "invalid_plan_profile_payload",
    });
  }
  return value as unknown as PlanProfileResponse;
}











